import { useEffect, useRef, useState, useCallback } from "react";
import { api } from "@/lib/api";
import { Skeleton } from "@/components/ui/skeleton";
import { Button } from "@/components/ui/button";
import { Loader2, RefreshCw, User, Rotate3d, Play, Pause, UserRound, Shirt } from "lucide-react";
import { toast } from "sonner";

const VIEWS = ["front", "left", "side", "back", "right"];
const VIEW_LABEL = { front: "Front", left: "¾ Left", side: "Side", back: "Back", right: "¾ Right" };

/**
 * The outfit worn on a model — or on YOU (profile photos). Starts with the front view,
 * "Spin" renders 5 angles you can drag through (or auto-rotate).
 */
export const LookImage = ({ outfitId, auto = true, className = "", testId = "outfit-look", allowMe = true, defaultWho = null, fit = "cover" }) => {
  const [who, setWho] = useState(() => defaultWho || localStorage.getItem("wardrobe.lookWho") || "model");
  const [hasProfile, setHasProfile] = useState(false);
  const [state, setState] = useState(auto ? "loading" : "idle"); // idle | loading | done | error
  const [bust, setBust] = useState(0);
  const [views, setViews] = useState(["front"]);
  const [idx, setIdx] = useState(0);
  const [spinning, setSpinning] = useState(false);
  const [playing, setPlaying] = useState(false);
  const dragRef = useRef(null);

  useEffect(() => {
    api.getProfile().then((p) => setHasProfile(p.has_face || p.has_body)).catch(() => {});
  }, []);

  const render = useCallback(async (target = who) => {
    setState("loading"); setViews(["front"]); setIdx(0); setPlaying(false);
    try {
      await api.renderOutfit(outfitId, "front", target);
      setBust(Date.now());
      setState("done");
    } catch (e) {
      const msg = e?.response?.data?.detail;
      if (msg) toast.error(msg);
      setState("error");
    }
  }, [outfitId, who]);

  useEffect(() => {
    if (!outfitId) return;
    if (auto) render(who);
    else setState("idle");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [outfitId]);

  const switchWho = (w) => {
    if (w === who) return;
    if (w === "me" && !hasProfile) { toast.info("Add a photo of yourself in Settings → Your Twin first"); return; }
    setWho(w); localStorage.setItem("wardrobe.lookWho", w);
    render(w);
  };

  const spin = async () => {
    setSpinning(true);
    try {
      const r = await api.renderSpin(outfitId, who);
      const ordered = VIEWS.filter((v) => r.views.includes(v));
      setViews(ordered); setIdx(0); setBust(Date.now()); setPlaying(true);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not render the spin");
    } finally {
      setSpinning(false);
    }
  };

  // autoplay
  useEffect(() => {
    if (!playing || views.length < 2) return;
    const t = setInterval(() => setIdx((i) => (i + 1) % views.length), 700);
    return () => clearInterval(t);
  }, [playing, views.length]);

  // drag to rotate
  const onPointerDown = (e) => { if (views.length < 2) return; dragRef.current = { x: e.clientX, idx }; setPlaying(false); };
  const onPointerMove = (e) => {
    if (!dragRef.current) return;
    const dx = e.clientX - dragRef.current.x;
    const step = Math.round(dx / 40);
    const n = views.length;
    setIdx((((dragRef.current.idx + step) % n) + n) % n);
  };
  const onPointerUp = () => { dragRef.current = null; };

  const WhoToggle = () => allowMe ? (
    <div className="absolute top-2 left-2 inline-flex rounded-full bg-background/85 backdrop-blur p-0.5 text-[11px] font-medium" data-testid={`${testId}-who-toggle`}>
      <button onClick={() => switchWho("model")} className={`px-2.5 py-1 rounded-full inline-flex items-center gap-1 transition-colors ${who === "model" ? "bg-foreground text-background" : "hover:bg-secondary"}`} data-testid={`${testId}-who-model`}><Shirt className="h-3 w-3" /> Model</button>
      <button onClick={() => switchWho("me")} className={`px-2.5 py-1 rounded-full inline-flex items-center gap-1 transition-colors ${who === "me" ? "bg-action text-action-foreground" : "hover:bg-secondary"}`} title={hasProfile ? "Render on you" : "Add your photo in Settings first"} data-testid={`${testId}-who-me`}><UserRound className="h-3 w-3" /> Me</button>
    </div>
  ) : null;

  if (state === "idle") {
    return (
      <div className={`relative overflow-hidden rounded-xl bg-secondary flex flex-col items-center justify-center gap-2 ${className}`} data-testid={`${testId}-idle`}>
        <User className="h-7 w-7 text-muted-foreground" />
        <Button size="sm" variant="secondary" className="rounded-full" onClick={() => render(who)} data-testid={`${testId}-render-button`}>{who === "me" && hasProfile ? "See it on me" : "See it on a model"}</Button>
      </div>
    );
  }
  if (state === "loading") {
    return (
      <div className={`relative overflow-hidden rounded-xl ${className}`} data-testid={`${testId}-loading`}>
        <Skeleton className="absolute inset-0" />
        <div className="absolute inset-0 flex flex-col items-center justify-center gap-2 text-xs text-muted-foreground">
          <Loader2 className="h-5 w-5 animate-spin text-action" />
          {who === "me" ? "Dressing you…" : "Dressing your model…"}
        </div>
      </div>
    );
  }
  if (state === "error") {
    return (
      <div className={`relative overflow-hidden rounded-xl bg-secondary flex flex-col items-center justify-center gap-2 text-xs text-muted-foreground ${className}`} data-testid={`${testId}-error`}>
        <WhoToggle />
        Couldn't render the look
        <Button size="sm" variant="secondary" className="rounded-full" onClick={() => render(who)}><RefreshCw className="h-3.5 w-3.5 mr-1.5" /> Try again</Button>
      </div>
    );
  }
  const view = views[idx] || "front";
  return (
    <div
      className={`relative overflow-hidden rounded-xl bg-secondary group select-none ${views.length > 1 ? "cursor-ew-resize" : ""} ${className}`}
      data-testid={testId}
      onPointerDown={onPointerDown} onPointerMove={onPointerMove} onPointerUp={onPointerUp} onPointerLeave={onPointerUp}
    >
      {/* preload all angles so dragging is instant */}
      {views.map((v) => (
        <img key={v} src={`${api.outfitLookUrl(outfitId, v, who)}&t=${bust}`} alt={`Outfit ${VIEW_LABEL[v]} view`} draggable={false}
             className={`absolute inset-0 h-full w-full ${fit === "contain" ? "object-contain" : "object-cover"} transition-opacity duration-150 ${v === view ? "opacity-100" : "opacity-0"}`} />
      ))}
      <WhoToggle />
      {views.length > 1 && (
        <div className="absolute top-2 right-2 rounded-full bg-background/85 backdrop-blur px-2 py-0.5 text-[10px] font-medium" data-testid={`${testId}-view-label`}>{VIEW_LABEL[view]}</div>
      )}
      <div className="absolute bottom-2 inset-x-2 flex items-center justify-between gap-2">
        {views.length > 1 ? (
          <div className="flex items-center gap-1">
            <button onClick={() => setPlaying((p) => !p)} className="inline-flex items-center gap-1 rounded-full bg-background/85 backdrop-blur px-2.5 py-1 text-[11px] font-medium" data-testid={`${testId}-play`}>
              {playing ? <Pause className="h-3 w-3" /> : <Play className="h-3 w-3" />} {playing ? "Pause" : "Spin"}
            </button>
            <div className="flex gap-1 ml-1">
              {views.map((v, i) => <button key={v} onClick={() => { setIdx(i); setPlaying(false); }} aria-label={VIEW_LABEL[v]} className={`h-1.5 rounded-full transition-all ${i === idx ? "w-4 bg-foreground" : "w-1.5 bg-foreground/40"}`} />)}
            </div>
          </div>
        ) : (
          <button onClick={spin} disabled={spinning} className="inline-flex items-center gap-1 rounded-full bg-background/85 backdrop-blur px-2.5 py-1 text-[11px] font-medium disabled:opacity-70" data-testid={`${testId}-spin-button`}>
            {spinning ? <Loader2 className="h-3 w-3 animate-spin" /> : <Rotate3d className="h-3 w-3" />} {spinning ? "Rendering angles…" : "Spin 360°"}
          </button>
        )}
        <button onClick={() => render(who)} className="inline-flex items-center gap-1 rounded-full bg-background/85 backdrop-blur px-2.5 py-1 text-[11px] font-medium opacity-0 group-hover:opacity-100 focus-visible:opacity-100 transition-opacity" aria-label="Re-render look" data-testid={`${testId}-rerender`}>
          <RefreshCw className="h-3 w-3" />
        </button>
      </div>
      {views.length > 1 && <p className="absolute bottom-9 inset-x-0 text-center text-[10px] text-muted-foreground pointer-events-none">drag to rotate</p>}
    </div>
  );
};
